import { Mail } from 'lucide-react';
import { useRef } from 'react';

import { Flex } from '@/components/base';
import TooltipV2 from '@/components/Tooltip/TooltipV2';
import PropertyValue from '@/components/Typography/PropertyValue';
import Typo from '@/components/Typography/Typo';
import { NOT_ASSIGNED_LABEL } from '@/configs/reserved-word';
import AgentSelectModal from '@/features/Agent/components/AgentSelectModal';
import useAgentStore from '@/features/Agent/stores';
import useFeedbackListStore from '@/stores/Feedback/FeedbackListStore';
import useDropdownStore from '@/stores/pure/DropdownStore';

import { useFeedbackDetailContext } from '../../context/FeedbackDetailContext';
import { PropertyRowWrapper } from './PropertyRowWrapper';
import type { CommonFieldProps } from './types';
import { getDropdownKey } from './utils';

export const AgentField = (
  props: CommonFieldProps & {
    feedback_alias: string;
    agent_alias?: string;
  },
) => {
  const { updateDetail } = useFeedbackDetailContext();
  const changeAgent = useFeedbackListStore((state) => state.changeAgent);
  const ref = useRef<HTMLDivElement>(null);
  const setCurrDropdown = useDropdownStore((state) => state.setCurrDropdown);
  const agentMap = useAgentStore((state) => state.agentMap);
  const onPropertyClick = () => {
    setCurrDropdown(getDropdownKey(props.feedback_alias, 'manager'));
  };

  if (!props.showFull && !props.agent_alias) return null;

  if (props.isStatic)
    return (
      <PropertyRowWrapper {...props}>
        <PropertyValue
          value={
            props.agent_alias ? (
              <Flex a="center" g={8}>
                <Typo type="label3">{agentMap[props.agent_alias]?.name}</Typo>
                {agentMap[props.agent_alias]?.email && (
                  <TooltipV2 tooltipNode={agentMap[props.agent_alias]?.email}>
                    <Mail size={14} className="text-gray-600" />
                  </TooltipV2>
                )}
              </Flex>
            ) : (
              <Typo type="label3">{NOT_ASSIGNED_LABEL}</Typo>
            )
          }
        />
      </PropertyRowWrapper>
    );

  return (
    <>
      <PropertyRowWrapper {...props}>
        <PropertyValue
          ref={ref}
          value={
            props.agent_alias ? (
              <Flex a="center" g={8}>
                <Typo type="label3">{agentMap[props.agent_alias]?.name}</Typo>
                {agentMap[props.agent_alias]?.email && (
                  <TooltipV2 tooltipNode={agentMap[props.agent_alias]?.email}>
                    <Mail size={14} className="text-gray-600" />
                  </TooltipV2>
                )}
              </Flex>
            ) : (
              <Typo type="label3">{NOT_ASSIGNED_LABEL}</Typo>
            )
          }
          onClick={onPropertyClick}
        />
      </PropertyRowWrapper>
      <AgentSelectModal
        selected={props.agent_alias || NOT_ASSIGNED_LABEL}
        useShortcut
        name={getDropdownKey(props.feedback_alias, 'manager')}
        onSelect={(key) => {
          updateDetail({
            manager: key,
          });
          changeAgent(props.feedback_alias, key);
        }}
        onClear={() => {
          updateDetail({
            manager: undefined,
          });
          changeAgent(props.feedback_alias, null);
        }}
        triggerEl={ref.current}
      />
    </>
  );
};
